import type { ReactNode } from 'react';
import { alpha, Avatar, Box, Card, CardActionArea, CardContent, Grid, Stack, Typography } from '@mui/material';
import { AccountBalanceOutlined, AutoAwesomeOutlined, ChevronRightOutlined, PercentOutlined, PublicOutlined, SettingsOutlined, TuneOutlined, VpnKeyOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader';
import { platformPath } from '@/config/appTarget';

/** The platform-wide configuration screens. Each is its own route; this is the one way in to all of them. */
const SETTINGS: { to: string; title: string; text: string; icon: ReactNode }[] = [
  { to: '/ai/configuration', title: 'Platform AI Configuration', text: 'The AI provider and model every company falls back to.', icon: <AutoAwesomeOutlined /> },
  { to: '/integrations/gst', title: 'GST Lookup', text: 'Gateway credentials used to verify GSTINs during onboarding.', icon: <VpnKeyOutlined /> },
  { to: '/onboarding-options', title: 'Setup Options', text: 'The choices a new workspace is offered while it is set up.', icon: <TuneOutlined /> },
  { to: '/banks', title: 'Banks & IFSC', text: 'The RBI bank and branch list every IFSC in TravelERP is checked against. Import, edit and export.', icon: <AccountBalanceOutlined /> },
  { to: '/hotel-gst', title: 'Hotel GST slabs', text: 'The dated, CA-verified GST slabs every hotel rate is charged at, per room per night.', icon: <PercentOutlined /> },
  { to: '/geography', title: 'Countries, states & cities', text: "The master list every company's countries, states and cities come from. Add, edit, import and export.", icon: <PublicOutlined /> },
];

/** Paths the Settings entry of the top bar lights up for. AI configuration already lights AI Management. */
export const PLATFORM_SETTINGS_PATHS = ['/settings', ...SETTINGS.map((s) => s.to).filter((to) => !to.startsWith('/ai/'))].map(platformPath);

export default function PlatformSettingsPage() {
  return (
    <Box>
      <PageHeader icon={<SettingsOutlined />} title="Settings" subtitle="Platform-wide configuration shared by every company." />
      <Grid container spacing={{ xs: 1.5, md: 2 }}>
        {SETTINGS.map((s) => (
          <Grid key={s.to} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card sx={{ height: '100%', transition: 'border-color .15s, box-shadow .15s', '&:hover': { borderColor: 'primary.main', boxShadow: 2 } }}>
              <CardActionArea component={RouterLink} to={platformPath(s.to)} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar variant="rounded" sx={(t) => ({ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(t.palette.primary.main, 0.1), color: 'primary.main', flexShrink: 0 })}>
                      {s.icon}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>{s.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{s.text}</Typography>
                    </Box>
                    <ChevronRightOutlined fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
